import { useToast } from "uiShadcn/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/nextjs";
import { sendForm } from "api/sendForm";
import { checkErrors, checkIfApplyWithCv, hasRequiredPersonalFields, prepareFormDefaultValues, prepareSubmitData } from "app/form/[formId]/(formTemplate)/firstStep/firstStepForm/helpers";
import { usePrepareSchema } from "app/form/[formId]/(formTemplate)/firstStep/firstStepForm/usePrepareSchema";
import type { FirstStepProps } from "app/form/[formId]/(formTemplate)/firstStep/types";
import { extractCompensationPackageData } from "forms/helpers/extractCompensationPackageData";
import { extractPersonalData } from "forms/helpers/extractPersonalData";
import { extractStepsElements, prepareElements } from "forms/helpers/stepsHelpers";
import { useFormErrors } from "forms/hooks/useFormErrors";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRef, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { logger } from "utils/helpers/logger";
import type { z } from "zod";
import { redirectToNextStep } from "./redirectFunction";
export const useFirstStepForm = ({
  formId,
  uiSchema,
  jsonSchema,
  candidateData,
  isTwoStepForm
}: FirstStepProps) => {
  const {
    toast
  } = useToast();
  const {
    formatMessage
  } = useIntl();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, startTransition] = useTransition();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    mapCodeErrorToFormError
  } = useFormErrors();

  // Url params from recruitment links
  const ruid = searchParams.get("ruid");
  const oid = searchParams.get("oid");
  const protocolOfferid = searchParams.get("protocolOfferid");
  const exsysrecid = searchParams.get("exsysrecid");
  const skkcfg = searchParams.get("skkcfg");
  const uiSchemaElements = prepareElements(uiSchema?.elements ?? []);
  const uiSteps = extractStepsElements(uiSchema);
  const {
    firstStep: {
      clauses,
      consents
    },
    secondStep
  } = uiSteps;
  const shouldRenderSecondStep = secondStep.length > 0 && isTwoStepForm;
  const uiFields = isTwoStepForm ? uiSteps.firstStepArray : uiSchemaElements;
  const personalDataFields = extractPersonalData(uiSchemaElements);
  const repeatEmail = personalDataFields?.email?.["x-json-schema-double-email-verification-required"];
  const renderSubmitButton = hasRequiredPersonalFields(personalDataFields);
  const clausesKeys = clauses?.["x-dictionary"]?.map(clause => clause.key);
  const consentKeys = consents?.["x-dictionary"]?.map(ct => ct.key);
  const compensationPackage = extractCompensationPackageData(uiSchema);
  const schema = usePrepareSchema({
    jsonSchema,
    isTwoStepForm,
    repeatEmail: !!repeatEmail
  });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: prepareFormDefaultValues(clausesKeys, candidateData)
  });
  const onSubmitHandler = async (data: Record<string, unknown>) => {
    startTransition(async () => {
      try {
        const formData = prepareSubmitData({
          data,
          oid,
          ruid,
          exsysrecid,
          skkcfg,
          protocolOfferid,
          consentKeys,
          compensationPackage
        });
        logger.log("submitting form", formData);
        const resp = await sendForm({
          id: formId,
          data: formData,
          applyStep: shouldRenderSecondStep ? 1 : undefined
        });
        checkErrors(resp, (fieldName, e_0) => {
          form.setError(fieldName, mapCodeErrorToFormError(e_0));
        });
        if (resp.isSuccess) {
          if (shouldRenderSecondStep) {
            toast({
              title: formatMessage({
                id: "on_first_step_success_message"
              }),
              variant: "success"
            });
          }
          redirectToNextStep({
            router,
            pathname,
            searchParams,
            shouldRenderSecondStep,
            id: resp.data?.id,
            withCv: checkIfApplyWithCv(formData)
          });
        }
      } catch (e) {
        Sentry.withScope(scope => {
          scope.setTag("feature", "submit-first-step");
          scope.setContext("Form data", {
            formId,
            isTwoStepForm
          });
          Sentry.captureException(e);
        });
        toast({
          title: formatMessage({
            id: "something_went_wrong"
          }),
          variant: "destructive"
        });
      }
    });
  };
  return {
    formRef,
    isSubmitting,
    form,
    onSubmitHandler,
    shouldRenderSecondStep,
    uiFields,
    renderSubmitButton
  };
};